import { useRef } from "react";

const useCommentsDraftSession = () => {
    const addDraftCleanupRequestedRef = useRef(false);
    const addDraftCommentIdRef = useRef(null);
    const isModalOpenRef = useRef(false);
    const modalModeRef = useRef("add");
    const addSessionCounterRef = useRef(0);
    const currentAddSessionIdRef = useRef(null);
    const sessionDraftIdMapRef = useRef({});
    const cancelledAddSessionsRef = useRef(new Set());

    const syncModalOpenState = (isOpen) => {
        isModalOpenRef.current = isOpen;
    };

    const syncModalModeState = (mode) => {
        modalModeRef.current = mode;
    };

    const startAddSession = () => {
        const sessionId = ++addSessionCounterRef.current;
        addDraftCleanupRequestedRef.current = false;
        addDraftCommentIdRef.current = null;
        currentAddSessionIdRef.current = sessionId;
        sessionDraftIdMapRef.current[sessionId] = null;
        cancelledAddSessionsRef.current.delete(sessionId);
        return sessionId;
    };

    const registerDraft = (sessionId, draftID) => {
        const numericDraftId = +draftID;
        addDraftCommentIdRef.current = numericDraftId;
        sessionDraftIdMapRef.current[sessionId] = numericDraftId;
        return numericDraftId;
    };

    const shouldCleanupDraftAfterCreate = (sessionId) => {
        const sessionCancelled = cancelledAddSessionsRef.current.has(sessionId);
        const sessionIsOutdated = currentAddSessionIdRef.current !== sessionId;
        const modalClosedOrSwitched = !isModalOpenRef.current || modalModeRef.current !== "add";
        return addDraftCleanupRequestedRef.current || sessionCancelled || sessionIsOutdated || modalClosedOrSwitched;
    };

    const getCurrentAddSessionId = () => currentAddSessionIdRef.current;

    const getDraftCommentID = (activeCommentId) => {
        const sessionId = currentAddSessionIdRef.current;
        return (sessionId && sessionDraftIdMapRef.current[sessionId]) || addDraftCommentIdRef.current || activeCommentId;
    };

    const requestCleanupForCurrentSession = () => {
        addDraftCleanupRequestedRef.current = true;
        const sessionId = currentAddSessionIdRef.current;
        if (sessionId) {
            cancelledAddSessionsRef.current.add(sessionId);
        }
    };

    const clearSession = (sessionId) => {
        addDraftCleanupRequestedRef.current = false;
        addDraftCommentIdRef.current = null;
        if (sessionId) {
            delete sessionDraftIdMapRef.current[sessionId];
            cancelledAddSessionsRef.current.delete(sessionId);
            if (currentAddSessionIdRef.current === sessionId) {
                currentAddSessionIdRef.current = null;
            }
        }
    };

    const clearCurrentSession = () => {
        clearSession(currentAddSessionIdRef.current);
    };

    return {
        syncModalOpenState,
        syncModalModeState,
        startAddSession,
        registerDraft,
        shouldCleanupDraftAfterCreate,
        getCurrentAddSessionId,
        getDraftCommentID,
        requestCleanupForCurrentSession,
        clearSession,
        clearCurrentSession,
    };
};

export default useCommentsDraftSession;
